import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { App } from './ui/App';
import { configureLogger, log } from './app/logger';
import { installGlobalHandlers } from './app/globalHandlers';
import { notifyUpdateReady } from './app/swUpdate';
import './ui/styles.css';

/**
 * Entry point.
 *
 * Order matters: the logger and the global handlers are installed before anything else runs, so an
 * error thrown during startup is captured rather than lost (OBS-1, NFR-8).
 */

configureLogger({
  console: import.meta.env.DEV,
  // OBS-1: the ring buffer is a production behavior. It is enabled in both modes here because the
  // harness's own debug output is part of what a venue run needs to be able to hand back.
  persist: true,
  now: () => Date.now(),
});

installGlobalHandlers();

log.info('boot', 'Touchline U-0 harness starting', {
  buildId: __BUILD_ID__,
  mode: import.meta.env.MODE,
  standalone:
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches,
});

/**
 * OBS-4 — `registerType: 'prompt'`. A force-reload is forbidden during a live state, so the update
 * is never applied automatically.
 *
 * Choosing `prompt` obliges us to *provide* one. An earlier build set `prompt` and only logged
 * `onNeedRefresh`, so the new worker sat in `waiting` for ever and an installed instance served its
 * original bundle permanently — a deployed fix simply never reached the device. `swUpdate` holds
 * the waiting worker; `UpdateBanner` surfaces it; the run lock decides when it may be applied.
 */
const updateSW = registerSW({
  onNeedRefresh() {
    log.info('sw', 'a new bundle is waiting; offering it to the operator');
    notifyUpdateReady(updateSW);
  },
  onRegisterError(error) {
    log.exception('sw', 'service worker registration failed', error);
  },
});

const container = document.getElementById('root');
if (!container) {
  // Not a silent failure: without this the page would render blank with no explanation.
  throw new Error('Root container #root is missing from index.html.');
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
